import { RoutesEnum, RoutesParametersEnum } from "@app/models/RoutesEnum";
import { CustomEndpointService } from "@app/services/CustomEndpointService";
import { CustomEndpointInterface } from "@dashart/dashart-gpt-shared-library";
import UserInterface from "@dashart/dashart-gpt-shared-library/dist/interfaces/UserInterface";
import { ToastMessageInterface } from "@app/interfaces/ToastMessageInterface";
import ButtonWithLink from "@view/components/buttonWithLink/ButtonWithLink";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Menubar } from "primereact/menubar";
import { Panel } from "primereact/panel";
import * as React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./CustomEndpointsScreen.scss";

export interface CustomEndpointsScreenProps {
    currentUser: UserInterface;
    showToast: (toast: ToastMessageInterface) => void;
}

/**
 * List screen showing all custom endpoints owned by the current user, with
 * inline actions (chat, edit, delete) and a top-level "create" button.
 *
 * @param props.currentUser the authenticated user
 * @param props.showToast callback to surface success/error notifications
 * @returns the rendered list panel
 */
const CustomEndpointsScreen: React.FunctionComponent<CustomEndpointsScreenProps> = (props: CustomEndpointsScreenProps) => {
    const { t } = useTranslation();
    const [entries, setEntries] = React.useState<CustomEndpointInterface[]>([]);

    /**
     * Fetches the user's endpoints and updates local state.
     * Shows an error toast and logs the failure if the request rejects.
     */
    const loadEntries = async () => {
        try {
            const result = await CustomEndpointService.getAll();
            setEntries(result ?? []);
        } catch (error) {
            console.log(error);
            props.showToast({
                summary: t('custom_endpoints.list_screen.error_loading'),
                severity: 'error'
            });
        }
    };

    useEffect(() => {
        loadEntries();
    }, []);

    /**
     * Renders the per-row chat action button. Returns null when the endpoint
     * is inactive so users can't navigate into a disabled endpoint's chat.
     *
     * @param rowData the row's CustomEndpoint
     * @returns the chat link or null when inactive
     */
    const cellParserChat = (rowData: CustomEndpointInterface) => {
        if (!rowData.active) {
            return null;
        }
        return <ButtonWithLink
            key={`btn-chat-${rowData.id}`}
            href={RoutesEnum.chatWithCustomEndpoint.replace(RoutesParametersEnum.publicId, rowData.publicId)}
            icon="pi pi-comments"
            label={t('custom_endpoints.actions.chat')}
            className="p-button-text mr-1"
        />;
    };

    /**
     * Renders the per-row edit and delete action buttons.
     *
     * @param rowData the row's CustomEndpoint
     * @returns the action button group
     */
    const cellParserActions = (rowData: CustomEndpointInterface) => {
        return <>
            <ButtonWithLink
                key={`btn-edit-${rowData.id}`}
                href={RoutesEnum.customEndpointEdit.replace(RoutesParametersEnum.endpointId, `${rowData.id}`)}
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text mr-1"
            />
            <ButtonWithLink
                key={`btn-delete-${rowData.id}`}
                href={RoutesEnum.customEndpointDelete.replace(RoutesParametersEnum.endpointId, `${rowData.id}`)}
                icon="pi pi-trash"
                className="p-button-rounded p-button-warning p-button-text mr-1"
            />
        </>;
    };

    /**
     * Renders a compact summary of the row's model list (first three names,
     * with a `(+N)` suffix when more exist).
     *
     * @param rowData the row's CustomEndpoint
     * @returns a human-readable model summary, or '-' when empty
     */
    const cellParserModels = (rowData: CustomEndpointInterface) => {
        const models = rowData.models ?? [];
        if (models.length === 0) return '-';
        if (models.length <= 3) return models.join(', ');
        return `${models.slice(0, 3).join(', ')} (+${models.length - 3})`;
    };

    /**
     * Renders a colored check/cross icon reflecting the row's active flag.
     *
     * @param rowData the row's CustomEndpoint
     * @returns the status icon element
     */
    const cellParserActive = (rowData: CustomEndpointInterface) => {
        return rowData.active
            ? <i className="pi pi-check-circle" style={{ color: 'var(--green-500)' }} />
            : <i className="pi pi-times-circle" style={{ color: 'var(--surface-400)' }} />;
    };

    /**
     * Renders the top/bottom action menubar containing the "Create" button.
     *
     * @param key suffix used to keep React keys unique when the menu is rendered twice
     * @returns the menubar element
     */
    const actionMenu = (key: string = '') => {
        return <Menubar
            className={'mt-2 mb-2'}
            end={
                <ButtonWithLink
                    key={`${key}-btn-add`}
                    className='mr-2'
                    label={t('custom_endpoints.actions.create')}
                    icon={'pi pi-fw pi-plus'}
                    href={RoutesEnum.customEndpointCreate}
                />
            }
        />;
    };

    return <div className='CustomEndpointsScreen d-list-screen'>
        <Panel header={t('custom_endpoints.list_screen.header')} className={'p-4'}>
            {actionMenu('top')}
            <div className={'table-container'}>
                <DataTable value={entries} rowHover={true} scrollable scrollHeight={'flex'} sortField="id" sortOrder={1}>
                    <Column field="id" header="ID" style={{ width: '5em' }} sortable />
                    <Column field="endpointSlug" header={t('custom_endpoints.fields.endpointSlug')} sortable />
                    <Column field="name" header={t('custom_endpoints.fields.name')} sortable />
                    <Column field="baseUrl" header={t('custom_endpoints.fields.baseUrl')} />
                    <Column header={t('custom_endpoints.fields.models')} body={cellParserModels} />
                    <Column header={t('custom_endpoints.fields.active')} body={cellParserActive} style={{ width: '6rem' }} />
                    <Column header={t('custom_endpoints.actions.chat')} body={cellParserChat} style={{ width: '10rem' }} />
                    <Column header={t('custom_endpoints.actions.header')} body={cellParserActions} style={{ width: '10rem' }} />
                </DataTable>
            </div>
            {actionMenu('bottom')}
        </Panel>
    </div>;
};

export default CustomEndpointsScreen;
